// Types and pure helpers for the Ads pages (pages/Ads.tsx, pages/AdsClient.tsx).
// No imports and only erasable types, so Node tests load it directly
// (test/ads-ui.test.js), like web-analytics.ts.

export type Metrics = {
  spend: number; impressions: number; clicks: number; conversions: number; calls: number;
  ctr: number | null; cpc: number | null; cpa: number | null; revenue: number | null; roas: number | null;
};
export type MetricKey = keyof Metrics;
export type Change = Partial<Record<MetricKey, number | null>>;
export type Period = { from: string; to: string; days: number; previous: { from: string; to: string; days: number } };
export type Budget = { budget: number; month_start: string; month_spend: number; used_pct: number | null; projected: number | null };
export type PlatformDay = { spend: number; clicks: number; conversions: number };
export type Day = PlatformDay & { date: string; by_platform: Record<string, PlatformDay>; unobserved?: boolean };
export type ClientRow = {
  id: number; name: string; platforms: string[]; compared_platforms: string[]; metrics: Metrics; previous: Metrics | null; change: Change;
  revenue_source: "jobs" | "ghl_won" | null; budget: Budget | null; open_anomalies: number;
  last_synced_at: string | null; stale: boolean;
};
export type AdsOverview = {
  period: Period; today: string; lsa_split_from: string | null; calls_from?: string | null;
  totals: Metrics & { revenue_clients: number }; previous_totals: Metrics | null; change: Change;
  comparison_clients: number; daily: Day[];
  platforms: { platform: string; spend: number; clicks: number; conversions: number; clients: number }[];
  clients: ClientRow[]; unavailable: string[];
};
export type Anomaly = { id: number; title: string; body: string; recommendation: string | null; severity: string; generated_at: string; repeats?: number; ids?: number[] };
export type Campaign = Metrics & { campaign_id: string; name: string; channel: string | null; account_label?: string | null; customer_id?: string | null };
export type ConversionAction = { name: string; conversions: number; value: number };
export type AdsChangeRow = { id: number; approval_id: number | null; kind: string; title: string; status: "applied" | "undone"; applied_at: string; day: string; by_name: string | null };

/** Calls per day for up to two weeks, per week (Monday start) beyond that, so every bar keeps a readable label. */
export function callBuckets(daily: { date: string; calls: number }[]): { label: string; value: number; weekly: boolean }[] {
  const short = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  if (daily.length <= 14) return daily.map((d) => ({ label: short(d.date), value: d.calls, weekly: false }));
  const weeks = new Map<string, number>();
  for (const d of daily) {
    const day = new Date(`${d.date}T12:00:00Z`);
    day.setUTCDate(day.getUTCDate() - ((day.getUTCDay() + 6) % 7));
    const key = day.toISOString().slice(0, 10);
    weeks.set(key, (weeks.get(key) || 0) + d.calls);
  }
  return [...weeks].map(([date, value]) => ({ label: short(date), value, weekly: true }));
}
export type DayCount = { date: string; calls: number };
export type CallsSection = {
  total: number; by_campaign: { campaign_id: string | null; name: string; calls: number }[];
  daily: DayCount[]; ctm: { total: number; daily: DayCount[] } | null;
};
export type RevenueSection =
  | { available: false; job_export?: { first_day: string; last_day: string } | null }
  | { available: true; source: "jobs" | "ghl_won"; revenue: number; roas: number | null; jobs: number; won_deals: number | null;
      /** Job revenue: the days inside the range that the job export covers (ROAS uses their spend). */
      covers?: { from: string; to: string } | null;
      by_campaign: { campaign: string; booked_jobs: number; completed_jobs: number; revenue: number }[] };
export type SummarySection = { metrics: Metrics; previous: Metrics | null; change: Change; compared_platforms: string[]; lsa_split_from?: string | null; calls_from?: string | null };
export type TrendSection = { daily: Day[]; lsa_split_from: string | null };
export type HealthSection = { anomalies: Anomaly[]; last_synced_at: string | null; stale: boolean };
export type Section = { key: string; audience: "staff" | "client"; data: unknown };
export type AdsReport = { client: { id: number; name: string; status: string; service_tier: string | null; has_account?: boolean }; detail_from?: string | null; period: Period; today: string; sections: Section[] };
export type SortKey = "name" | "spend" | "clicks" | "cpc" | "conversions" | "cpa" | "calls" | "budget";
export type TrendMetric = "spend" | "clicks" | "conversions";

/** A report section's data, or `unavailable` when its source failed on the server. */
export function section<T>(report: AdsReport | null, key: string): { data: T | null; unavailable: boolean } {
  const s = report?.sections.find((x) => x.key === key);
  if (!s) return { data: null, unavailable: false };
  const d = s.data as { error?: string } | null;
  if (d && typeof d === "object" && d.error === "unavailable") return { data: null, unavailable: true };
  return { data: s.data as T, unavailable: false };
}

/** Which way is good news. Spend is neither: more spend is a plan, not a result. */
const GOOD_WHEN: Record<MetricKey, "up" | "down" | "neutral"> = {
  spend: "neutral", impressions: "up", clicks: "up", conversions: "up", calls: "up",
  ctr: "up", cpc: "down", cpa: "down", revenue: "up", roas: "up",
};
export function changeTone(metric: MetricKey, change: number | null | undefined): "positive" | "negative" | "neutral" | null {
  if (change === null || change === undefined) return null;
  const good = GOOD_WHEN[metric];
  if (good === "neutral" || change === 0) return "neutral";
  return (change > 0) === (good === "up") ? "positive" : "negative";
}

/** Sorted copy; rows without a value go last in both directions, ties by name. */
export function sortClients(rows: ClientRow[], key: SortKey, dir: "asc" | "desc"): ClientRow[] {
  const value = (r: ClientRow): number | string | null =>
    key === "name" ? r.name.toLowerCase() : key === "budget" ? r.budget?.used_pct ?? null : r.metrics[key];
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (x === null && y === null) return a.name.localeCompare(b.name);
    if (x === null) return 1;
    if (y === null) return -1;
    const c = x < y ? -1 : x > y ? 1 : 0;
    return (dir === "asc" ? c : -c) || a.name.localeCompare(b.name);
  });
}

export function filterClients(rows: ClientRow[], { platform, q }: { platform: string; q: string }): ClientRow[] {
  const needle = q.trim().toLowerCase();
  return rows.filter((r) => (platform === "all" || r.platforms.includes(platform)) && (!needle || r.name.toLowerCase().includes(needle)));
}

/**
 * One value per day, over the whole range. Accounts that only run LSA have LSA rows
 * from their first sync; accounts that mix both carry LSA inside Google Ads before the
 * split (the chart says so under the line).
 */
export function trendPoints(daily: Day[], metric: TrendMetric, platform: string): { date: string; value: number | null }[] {
  return daily.map((d) => ({ date: d.date, value: d.unobserved ? null : platform === "all" ? d[metric] : d.by_platform[platform]?.[metric] ?? 0 }));
}

/** An export outside the selected dates cannot establish that no jobs were booked. */
export function jobRevenueEmptyMessage(span: { first_day: string; last_day: string }, period: Pick<Period, "from" | "to">): string {
  const overlaps = span.first_day <= period.to && span.last_day >= period.from;
  const message = overlaps ? "No booked jobs are recorded in this range." : "The job export does not cover this date range.";
  return `${message} The job export covers ${shortDate(span.first_day)} to ${shortDate(span.last_day)}.`;
}

/** "$2,220": whole dollars where rows are compared against each other. */
export const fmtMoneyWhole = (v: number | null | undefined) =>
  v === null || v === undefined || Number.isNaN(v) ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

/** The note under the totals: what the change figures compare, and what they leave out. */
export function comparisonNotes(lsaSplitFrom: string | null | undefined, callsFrom: string | null | undefined, shortDate: (d: string) => string) {
  const notes: string[] = [];
  if (lsaSplitFrom) notes.push(`Comparisons count LSA as Google Ads: before ${shortDate(lsaSplitFrom)}, LSA for accounts that also run Search was filed under Google Ads.`);
  if (callsFrom) notes.push(`Calls are recorded from ${shortDate(callsFrom)}, so they are not compared.`);
  return notes.join(" ");
}

const missing = (v: number | null | undefined): v is null | undefined => v === null || v === undefined || Number.isNaN(v);
export const fmtInt = (v: number | null | undefined) => (missing(v) ? "—" : new Intl.NumberFormat("en-US").format(Math.round(v)));
export const fmtNum1 = (v: number | null | undefined) => (missing(v) ? "—" : new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(v));
export const fmtCtr = (v: number | null | undefined) => (missing(v) ? "—" : `${(v * 100).toFixed(2)}%`);
export const fmtRoas = (v: number | null | undefined) => (missing(v) ? "—" : `${v.toFixed(2)}×`);

/** Google's campaign types as people say them. */
export function channelName(channel: string | null | undefined): string {
  const names: Record<string, string> = { SEARCH: "Search", PERFORMANCE_MAX: "Performance Max", LOCAL_SERVICES: "LSA", DISPLAY: "Display", VIDEO: "Video", DEMAND_GEN: "Demand Gen", SHOPPING: "Shopping", MULTI_CHANNEL: "Multi-channel", SMART: "Smart" };
  if (!channel) return "—";
  return names[channel] || channel.toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

export function platformName(p: string): string {
  // "LSA" on staff pages: the team's own word, and the one in the account labels.
  const names: Record<string, string> = { google_ads: "Google Ads", lsa: "LSA", meta: "Meta", bing: "Microsoft Ads", youtube: "YouTube" };
  return names[p] || p.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** "2026-09-21" -> "Sep 21", read as a calendar date (no time zone shift). */
export function shortDate(ymd: string): string {
  const [y, m, d] = ymd.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Platforms in a fixed order (Google Ads, LSA, YouTube, then the rest), so toggles don't move between ranges. */
const PLATFORM_ORDER = ["google_ads", "lsa", "youtube"];
export const sortPlatforms = (keys: string[]) =>
  [...keys].sort((a, b) => (PLATFORM_ORDER.indexOf(a) + 1 || 99) - (PLATFORM_ORDER.indexOf(b) + 1 || 99) || a.localeCompare(b));
